import React, { useState } from 'react';
import styled from 'styled-components';
import { TextField, Button, CircularProgress } from '@mui/material';
import { Helmet } from 'react-helmet';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/xtreme.png';  // Adjust path as needed

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      console.log('Submitting form:', formData);

      // Validate form data
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.message) {
        throw new Error('Please fill in all fields');
      }

      const response = await fetch('https://xtreme-backend.onrender.com/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
        mode: 'cors'
      });

      console.log('Server response status:', response.status);
      
      let data;
      try {
        data = await response.json();
        console.log('Server response data:', data);
      } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        throw new Error('Failed to parse server response');
      }

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to send message');
      }

      setMessage('Thank you for contacting us! We will get back to you soon.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      console.error('Contact form error:', err);
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Xtreme Roofing & Construction</title>
        <meta name="description" content="Get in touch with Xtreme Roofing & Construction for professional roofing and construction services." />
      </Helmet>

      <PublicNav>
        <NavContainer>
          <LogoLink to="/">
            <img src={logo} alt="Xtreme Roofing" />
          </LogoLink>
          <NavLinks>
            <NavLink to="/">Home</NavLink>
            <NavLink to="/calculator">Get Estimate</NavLink>
            <NavLink to="/contact" className="active">Contact</NavLink>
            <NavLink to="/login">Login</NavLink>
          </NavLinks>
        </NavContainer>
      </PublicNav>

      <HeroSection>
        <HeroContent>
          <h1>Contact Us</h1>
          <p>We're here to help with your construction needs</p>
        </HeroContent>
      </HeroSection>

      <ContactContainer>
        <ContactGrid>
          <ContactInfo>
            <InfoTitle>Get In Touch</InfoTitle>
            <InfoText>
              Have questions about our services? Need a quote? We're here to help!
            </InfoText>
            
            <ContactDetails>
              <DetailItem>
                <IconWrapper>
                  <FaPhone />
                </IconWrapper>
                <div>
                  <DetailLabel>Phone</DetailLabel>
                  <DetailValue>(682) 719-2877</DetailValue>
                </div>
              </DetailItem>

              <DetailItem>
                <IconWrapper>
                  <FaEnvelope />
                </IconWrapper>
                <div>
                  <DetailLabel>Email</DetailLabel>
                  <DetailValue>sales@xtremeroofingandconstruction.net</DetailValue>
                </div>
              </DetailItem>

              <DetailItem>
                <IconWrapper>
                  <FaMapMarkerAlt />
                </IconWrapper>
                <div>
                  <DetailLabel>Location</DetailLabel>
                  <DetailValue>DFW Metroplex</DetailValue>
                </div>
              </DetailItem>
            </ContactDetails>
          </ContactInfo>

          <ContactForm onSubmit={handleSubmit}>
            {message && <SuccessMessage>{message}</SuccessMessage>}
            {error && <ErrorMessage>{error}</ErrorMessage>}

            <FormGrid>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Message"
                multiline
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                fullWidth
                className="message-field"
              />
            </FormGrid>

            <SubmitButton
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Send Message'}
            </SubmitButton>
          </ContactForm>
        </ContactGrid>
      </ContactContainer>
    </>
  );
};

const PublicNav = styled.nav`
  background:rgb(13, 14, 14);  // Light grayish color
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-bottom: 1px solid #e9ecef;  // Subtle border
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background:rgb(7, 7, 7);  // Matching the nav background
`;

const LogoLink = styled(Link)`
  img {
    height: 100px;
    width: auto;
    object-fit: contain;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
`;

const NavLink = styled(Link)`
  color: #495057;  // Darker text for better contrast
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    color: #FF5722;
    background: rgba(255, 87, 34, 0.05);
  }

  &.active {
    color: #FF5722;
    background: rgba(255, 87, 34, 0.1);
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)),
              url('/images/contact-hero.jpg') center/cover;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-top: 72px;
`;

const HeroContent = styled.div`
  h1 {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
    opacity: 0.9;
  }
`;

const ContactContainer = styled.div`
  padding: 4rem 2rem;
  background: #f5f5f5;
`;

const ContactGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  background: #FF5722;
  color: white;
  padding: 2rem;
  border-radius: 16px;
  height: fit-content;
`;

const InfoTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const InfoText = styled.p`
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const DetailLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const DetailValue = styled.div`
  font-weight: 500;
`;

const ContactForm = styled.form`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .message-field {
    grid-column: 1 / -1;
  }
`;

const SubmitButton = styled(Button)`
  && {
    background: #FF5722;
    padding: 1rem;
    font-size: 1.1rem;
    
    &:hover {
      background: #F4511E;
    }
  }
`;

const SuccessMessage = styled.div`
  background: #E8F5E9;
  color: #2E7D32;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: #FFEBEE;
  color: #C62828;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

export default Contact; 